/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  RadarEntry,
  RadarQuadrant,
  RadarRing,
  TechRadarLoaderResponse,
} from '@backstage-community/plugin-tech-radar-common';

const rings = new Array<RadarRing>();
rings.push({ id: 'adopt', name: 'ADOPT', color: '#5BA300' });
rings.push({ id: 'trial', name: 'TRIAL', color: '#009EB0' });
rings.push({ id: 'assess', name: 'ASSESS', color: '#C7BA00' });
rings.push({ id: 'hold', name: 'HOLD', color: '#E09B96' });

const quadrants = new Array<RadarQuadrant>();
quadrants.push({ id: 'techniques', name: 'Techniques' });
quadrants.push({ id: 'tools', name: 'Tools' });
quadrants.push({ id: 'languages-frameworks', name: 'Languages & Frameworks' });
quadrants.push({ id: 'platforms', name: 'Platforms' });

const entries = new Array<RadarEntry>();

// Languages & Frameworks
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  key: 'react',
  id: 'react',
  title: 'React',
  quadrant: 'languages-frameworks',
  links: [
    {
      url: 'https://react.dev/',
      title: 'React',
    },
  ],
  description:
    'React lets you build user interfaces out of individual pieces called components. Create your own React components like Thumbnail, LikeButton, and Video. Then combine them into entire screens, pages, and apps.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  key: 'typescript',
  id: 'typescript',
  title: 'TypeScript',
  quadrant: 'languages-frameworks',
  links: [
    {
      url: 'https://www.typescriptlang.org/',
      title: 'TypeScript',
    },
  ],
  description: 'TypeScript is JavaScript with syntax for types.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://www.apollographql.com/docs/apollo-server/',
      title: 'Introduction to Apollo Server',
    },
  ],
  key: 'apollo-server',
  id: 'apollo-server',
  title: 'Apollo Server',
  quadrant: 'languages-frameworks',
  description:
    "Apollo Server is an open-source, spec-compliant GraphQL server that's compatible with any GraphQL client, including Apollo Client.",
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'In use across engineering repositories',
    },
  ],
  links: [
    {
      url: 'https://zod.dev/',
      title: 'Zod',
    },
  ],
  key: 'zod',
  id: 'zod',
  title: 'Zod',
  quadrant: 'languages-frameworks',
  description:
    'TypeScript-first schema validation with static type inference.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Replaces the Serverless Framework',
    },
  ],
  links: [
    {
      url: 'https://sst.dev/',
      title: 'SST',
    },
  ],
  key: 'sst',
  id: 'sst',
  title: 'SST',
  quadrant: 'languages-frameworks',
  description:
    'Framework for defining and deploying serverless applications on AWS as code. Replaces the Serverless Framework.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Approved for infrastructure as code',
    },
  ],
  links: [
    {
      url: 'https://www.pulumi.com/',
      title: 'Pulumi',
    },
  ],
  key: 'pulumi',
  id: 'pulumi',
  title: 'Pulumi',
  quadrant: 'languages-frameworks',
  description:
    'Infrastructure as code using general-purpose languages. Approved for infrastructure, and the engine behind SST.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Approved for infrastructure as code',
    },
  ],
  links: [
    {
      url: 'https://www.terraform.io/',
      title: 'Terraform',
    },
  ],
  key: 'terraform',
  id: 'terraform',
  title: 'Terraform',
  quadrant: 'languages-frameworks',
  description:
    'Declarative infrastructure as code. Approved for infrastructure alongside Pulumi.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'hold',
      date: new Date('2026-10-13'),
      description: 'Deprecated; replaced by SST',
    },
  ],
  links: [
    {
      url: 'https://www.serverless.com/',
      title: 'Serverless Framework',
    },
  ],
  key: 'serverless-framework',
  id: 'serverless-framework',
  title: 'Serverless Framework',
  quadrant: 'languages-frameworks',
  description:
    'Being deprecated across engineering. Do not start new work on it; migrate existing services to SST.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'hold',
      date: new Date('2026-10-13'),
      description: 'Not used and no plan to use',
    },
  ],
  links: [
    {
      url: 'https://aws.amazon.com/cdk/',
      title: 'AWS CDK',
    },
  ],
  key: 'aws-cdk',
  id: 'aws-cdk',
  title: 'AWS CDK',
  quadrant: 'languages-frameworks',
  description:
    'Not used anywhere in engineering and not planned. Use SST, Pulumi or Terraform for infrastructure.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'hold',
      date: new Date('2026-10-13'),
      description: 'No longer used for new work',
    },
  ],
  links: [
    {
      url: 'https://lerna.js.org/',
      title: 'Lerna',
    },
  ],
  key: 'lerna',
  id: 'lerna',
  title: 'Lerna',
  quadrant: 'languages-frameworks',
  description:
    'Monorepo tooling that engineering no longer wants to use. Do not start new work on it.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Declared as the package manager in most active repos',
    },
  ],
  links: [
    {
      url: 'https://pnpm.io/',
      title: 'pnpm',
    },
  ],
  key: 'pnpm',
  id: 'pnpm',
  title: 'pnpm',
  quadrant: 'languages-frameworks',
  description:
    'Fast, disk-efficient package manager. Used by most active engineering repositories.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Minimum Node version in around 95 active repositories',
    },
  ],
  links: [
    {
      url: 'https://nodejs.org/',
      title: 'Node.js',
    },
  ],
  key: 'nodejs-24',
  id: 'nodejs-24',
  title: 'Node.js 24',
  quadrant: 'languages-frameworks',
  description:
    'The Node.js runtime version that a large share of active repositories now require as a minimum.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'trial',
      date: new Date('2026-10-13'),
      description: 'In use alongside Jest in a minority of repos',
    },
  ],
  links: [
    {
      url: 'https://vitest.dev/',
      title: 'Vitest',
    },
  ],
  key: 'vitest',
  id: 'vitest',
  title: 'Vitest',
  quadrant: 'languages-frameworks',
  description:
    'Vite-native unit test framework. Used in a minority of repositories while Jest remains the most common.',
});

// Tools
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://github.com',
      title: 'Learn more',
    },
  ],
  key: 'github-actions',
  id: 'github-actions',
  title: 'GitHub Actions',
  quadrant: 'tools',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://miro.com/',
      title: 'Miro',
    },
  ],
  key: 'miro',
  id: 'miro',
  title: 'Miro',
  quadrant: 'tools',
  description: 'The leading visual collaboration platform',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Deployed our new ATG Backstage',
    },
  ],
  links: [
    {
      url: 'https://backstage.io/',
      title: 'Backstage Docs',
    },
  ],
  key: 'backstage',
  id: 'backstage',
  title: 'Backstage',
  quadrant: 'tools',
  description: 'An open platform for building developer portals',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Standard AI tool for engineering',
    },
  ],
  links: [
    {
      url: 'https://claude.com/product/claude-code',
      title: 'Claude Code',
    },
  ],
  key: 'claude',
  id: 'claude',
  title: 'Claude',
  quadrant: 'tools',
  description:
    'The AI tool used across engineering, including Claude Code for development.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Internal skills for Claude Code',
    },
  ],
  links: [
    {
      url: 'https://skills.atg.engineering/',
      title: 'ATG skills',
    },
  ],
  key: 'claude-skills',
  id: 'claude-skills',
  title: 'claude-skills',
  quadrant: 'tools',
  description:
    'Our internal repository of skills for Claude Code.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Being rolled out to all repositories',
    },
  ],
  links: [
    {
      url: 'https://cycode.com/',
      title: 'Cycode',
    },
  ],
  key: 'cycode',
  id: 'cycode',
  title: 'Cycode',
  quadrant: 'tools',
  description:
    'Application security platform for scanning code, dependencies and secrets. Being rolled out to all repositories.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Being rolled out to all repositories',
    },
  ],
  links: [
    {
      url: 'https://docs.renovatebot.com/',
      title: 'Renovate',
    },
  ],
  key: 'renovate',
  id: 'renovate',
  title: 'Renovate',
  quadrant: 'tools',
  description:
    'Automated dependency update pull requests. Being rolled out to all repositories.',
});

// Techniques
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  key: 'multi-region-deployments',
  id: 'multi-region-deployments',
  title: 'Multi Region Deployments',
  quadrant: 'techniques',
  description: 'Deploy applications which are specific to regions and brands.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://aws.amazon.com/microservices/',
      title: 'AWS: What are Microservices',
    },
  ],
  key: 'microservices',
  id: 'microservices',
  title: 'Microservices',
  quadrant: 'techniques',
  description:
    'Microservices are an architectural and organizational approach to software development where software is composed of small independent services that communicate over well-defined APIs. These services are owned by small, self-contained teams.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://bradfrost.com/blog/post/atomic-web-design/',
      title: 'Blog: Atomic Design',
    },
    {
      url: 'https://atomicdesign.bradfrost.com/',
      title: 'Book: Atomic Design',
    },
  ],
  key: 'atomic-design',
  id: 'atomic-design',
  title: 'Atomic Design',
  quadrant: 'techniques',
  description: 'Atomic design is methodology for creating design systems.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'assess',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://martinfowler.com/articles/micro-frontends.html',
      title: 'Martin Fowler: Micro Frontends',
    },
  ],
  key: 'micro-frontends',
  id: 'micro-frontends',
  title: 'Micro Frontends',
  quadrant: 'techniques',
  description:
    'Micro frontends are all about slicing up big and scary things into smaller, more manageable pieces, and then being explicit about the dependencies between them.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'In use in around a quarter of active repositories',
    },
  ],
  links: [
    {
      url: 'https://docs.claude.com/en/docs/claude-code/memory',
      title: 'Claude Code memory and CLAUDE.md',
    },
  ],
  key: 'claude-md',
  id: 'claude-md',
  title: 'CLAUDE.md',
  quadrant: 'techniques',
  description:
    'Keep project guidance for Claude in a CLAUDE.md file in each repository so the AI tool follows repo conventions.',
});

// Platforms
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://aws.amazon.com/eventbridge/',
      title: 'Amazon EventBridge',
    },
  ],
  key: 'aws-event-bridge',
  id: 'aws-event-bridge',
  title: 'AWS Event Bridge',
  quadrant: 'platforms',
  description:
    'Build event-driven applications at scale across AWS, existing systems, or SaaS applications.',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://aws.amazon.com/dynamodb/',
      title: 'Amazon DynamoDB',
    },
  ],
  key: 'aws-dynamodb',
  id: 'aws-dynamodb',
  title: 'Amazon DynamoDB',
  quadrant: 'platforms',
  description:
    'Fast, flexible NoSQL database service for single-digit millisecond performance at any scale',
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2023-05-16'),
      description: 'Migrated from Miro',
    },
  ],
  links: [
    {
      url: 'https://umbraco.com/products/umbraco-heartcore/',
      title: 'Umbraco Heartcore',
    },
  ],
  key: 'umbraco-heartcore',
  id: 'umbraco-heartcore',
  title: 'Umbraco Heartcore',
  quadrant: 'platforms',
  description: "A headless CMS that's heartful and hardcore.",
});
entries.push({
  timeline: [
    {
      moved: 0,
      ringId: 'adopt',
      date: new Date('2026-10-13'),
      description: 'Internal agent platform, being rolled out',
    },
  ],
  key: 'ghostlight',
  id: 'ghostlight',
  title: 'Ghostlight',
  quadrant: 'platforms',
  description:
    'Our internal agent platform, currently being rolled out. Used to build and run agents, including Slack-routed and webhook-triggered agents.',
});

export const techRadarData: TechRadarLoaderResponse = {
  entries,
  quadrants,
  rings,
};
